import React from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import BrightnessAutoRoundedIcon from "@mui/icons-material/BrightnessAutoRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import SettingsCardHeader, {
  getSettingsCardShellSx,
} from "./SettingsCardHeader.jsx";

const OPTIONS = [
  {
    value: "auto",
    label: "Sync with system",
    description: "Follow your device's light or dark setting",
    Icon: BrightnessAutoRoundedIcon,
    preview: "auto",
  },
  {
    value: "light",
    label: "Light",
    description: "Bright theme, easy to read in daylight",
    Icon: LightModeRoundedIcon,
    preview: "light",
  },
  {
    value: "dark",
    label: "Dark",
    description: "Dimmed theme for low-light environments",
    Icon: DarkModeRoundedIcon,
    preview: "dark",
  },
];

function PreviewMockup({ kind }) {
  const frame = {
    width: "100%",
    height: 92,
    borderRadius: 1.25,
    overflow: "hidden",
    display: "flex",
    border: "1px solid",
    borderColor: "rgba(15, 23, 42, 0.08)",
  };

  if (kind === "auto") {
    return (
      <Box sx={frame}>
        <Box sx={{ width: "50%", bgcolor: "#ffffff", display: "flex" }}>
          <Box sx={{ width: 18, bgcolor: "#eef2f8" }} />
          <Box sx={{ flex: 1, p: 1, minWidth: 0 }}>
            <Box sx={{ height: 6, width: "65%", bgcolor: "#0f2b46", borderRadius: 0.5, mb: 0.75 }} />
            <Box sx={{ height: 5, width: "85%", bgcolor: "#dbe2ec", borderRadius: 0.5, mb: 0.5 }} />
            <Box sx={{ height: 5, width: "70%", bgcolor: "#dbe2ec", borderRadius: 0.5 }} />
          </Box>
        </Box>
        <Box sx={{ width: "50%", bgcolor: "#0f172a", display: "flex" }}>
          <Box sx={{ width: 18, bgcolor: "#1e293b" }} />
          <Box sx={{ flex: 1, p: 1, minWidth: 0 }}>
            <Box sx={{ height: 6, width: "65%", bgcolor: "#60a5fa", borderRadius: 0.5, mb: 0.75 }} />
            <Box sx={{ height: 5, width: "85%", bgcolor: "#334155", borderRadius: 0.5, mb: 0.5 }} />
            <Box sx={{ height: 5, width: "70%", bgcolor: "#334155", borderRadius: 0.5 }} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (kind === "light") {
    return (
      <Box sx={{ ...frame, bgcolor: "#ffffff" }}>
        <Box sx={{ width: 22, bgcolor: "#eef2f8" }} />
        <Box sx={{ flex: 1, p: 1.25, minWidth: 0 }}>
          <Box sx={{ height: 7, width: "55%", bgcolor: "#0f2b46", borderRadius: 0.5, mb: 1 }} />
          <Box sx={{ height: 5, width: "85%", bgcolor: "#dbe2ec", borderRadius: 0.5, mb: 0.5 }} />
          <Box sx={{ height: 5, width: "70%", bgcolor: "#dbe2ec", borderRadius: 0.5, mb: 0.5 }} />
          <Box sx={{ height: 5, width: "60%", bgcolor: "#dbe2ec", borderRadius: 0.5 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ ...frame, bgcolor: "#0f172a", borderColor: "rgba(255, 255, 255, 0.08)" }}>
      <Box sx={{ width: 22, bgcolor: "#1e293b" }} />
      <Box sx={{ flex: 1, p: 1.25, minWidth: 0 }}>
        <Box sx={{ height: 7, width: "55%", bgcolor: "#60a5fa", borderRadius: 0.5, mb: 1 }} />
        <Box sx={{ height: 5, width: "85%", bgcolor: "#334155", borderRadius: 0.5, mb: 0.5 }} />
        <Box sx={{ height: 5, width: "70%", bgcolor: "#334155", borderRadius: 0.5, mb: 0.5 }} />
        <Box sx={{ height: 5, width: "60%", bgcolor: "#334155", borderRadius: 0.5 }} />
      </Box>
    </Box>
  );
}

/**
 * Visual theme picker shared by Admin, Staff, SuperAdmin, and Customer portals.
 * Wraps content in the same Card + SettingsCardHeader shell as Logo Settings.
 */
export default function ThemeAppearancePanel({
  adminThemePreference,
  setAdminThemePreference,
  helpText,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const innerPanelBg = isDark ? "#252f3f" : "#fafafa";
  const innerPanelBorder = isDark ? "#2d3b4e" : "#dee2e6";

  return (
    <Card
      sx={{
        ...getSettingsCardShellSx(theme),
        maxWidth: { xs: "100%", sm: 700 },
        minWidth: 0,
      }}
    >
      <SettingsCardHeader
        title="Theme & appearance"
        icon={PaletteRoundedIcon}
        centerIconWithTitle
      />

      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, width: "100%", boxSizing: "border-box" }}>
        <Box
          sx={{
            width: "100%",
            boxSizing: "border-box",
            p: { xs: 2, sm: 2.5 },
            backgroundColor: innerPanelBg,
            border: `1px solid ${innerPanelBorder}`,
            borderRadius: 2,
          }}
        >
          <Box
            role="radiogroup"
            aria-label="Theme preference"
            sx={{
              display: "grid",
              width: "100%",
              boxSizing: "border-box",
              gap: { xs: 1.25, sm: 1.5 },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              maxWidth: 620,
              mx: "auto",
            }}
          >
            {OPTIONS.map((opt) => {
              const selected = adminThemePreference === opt.value;
              const Icon = opt.Icon;
              return (
                <Paper
                  key={opt.value}
                  elevation={0}
                  role="radio"
                  aria-checked={selected}
                  tabIndex={0}
                  onClick={() => setAdminThemePreference(opt.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setAdminThemePreference(opt.value);
                    }
                  }}
                  sx={{
                    position: "relative",
                    p: 1.5,
                    minWidth: 0,
                    cursor: "pointer",
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: selected ? "primary.main" : "divider",
                    bgcolor: "background.paper",
                    transition:
                      "border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease",
                    boxShadow: selected
                      ? `0 0 0 3px ${
                          isDark
                            ? "rgba(96, 165, 250, 0.22)"
                            : "rgba(25, 118, 210, 0.14)"
                        }`
                      : "none",
                    "&:hover": {
                      borderColor: selected ? "primary.main" : "primary.light",
                      transform: "translateY(-1px)",
                    },
                    "&:focus-visible": {
                      outline: "none",
                      boxShadow: `0 0 0 3px ${
                        isDark
                          ? "rgba(96, 165, 250, 0.32)"
                          : "rgba(25, 118, 210, 0.28)"
                      }`,
                    },
                  }}
                >
                  <PreviewMockup kind={opt.preview} />

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 1.25, minWidth: 0 }}
                  >
                    <Icon
                      sx={{
                        fontSize: 20,
                        flexShrink: 0,
                        color: selected ? "primary.main" : "text.secondary",
                      }}
                    />
                    <Typography
                      fontWeight={600}
                      sx={{
                        fontSize: "0.92rem",
                        lineHeight: 1.3,
                        color: selected ? "primary.main" : "text.primary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      }}
                    >
                      {opt.label}
                    </Typography>
                    {selected && (
                      <CheckCircleRoundedIcon
                        aria-hidden
                        sx={{ ml: "auto", flexShrink: 0, color: "primary.main", fontSize: 20 }}
                      />
                    )}
                  </Stack>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      lineHeight: 1.45,
                    }}
                  >
                    {opt.description}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          {helpText ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2.25, lineHeight: 1.55, maxWidth: 620 }}
            >
              {helpText}
            </Typography>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}
